import { cur } from '@/lib/currency';
import { Fragment, useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CheckCircle, Clock, XCircle, Calendar, DollarSign, FileText, Send,
  LogIn, LogOut, AlertTriangle, User, Banknote, Briefcase, Phone, Mail,
  Building, Wallet, TrendingDown, Receipt, Bell, BellRing,
  Plane, Coffee, Hand, RotateCcw, GraduationCap, ScrollText,
  CalendarOff, ShieldAlert, ArrowUpDown, Repeat, CreditCard, HeartPulse,
  ClipboardList, ChevronRight, MapPin, Loader2
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { NotLinkedNotice, isNotLinked } from '@/components/NotLinkedNotice';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday } from '@/lib/dateRange';
import { PaymentConfirmationsPanel } from '@/components/salary/PaymentConfirmationsPanel';

const getStatusBadge = (s) => {
  if (s === 'approved' || s === 'completed') return <Badge className="bg-success/20 text-success dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle size={11} className="mr-1" />{s}</Badge>;
  if (s === 'rejected') return <Badge className="bg-error/20 text-error dark:bg-red-900/30 dark:text-red-400"><XCircle size={11} className="mr-1" />{s}</Badge>;
  if (s === 'active') return <Badge className="bg-info/20 text-info dark:bg-blue-900/30 dark:text-blue-400"><Banknote size={11} className="mr-1" />{s}</Badge>;
  return <Badge className="bg-warning/20 text-warning dark:bg-amber-900/30 dark:text-amber-400"><Clock size={11} className="mr-1" />{s}</Badge>;
};

function LeaveBalanceBar({ label, used, total, color }) {
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? (used / total) * 100 : 0;
  return (
    <div data-testid={`leave-balance-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-medium dark:text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">{remaining}/{total} days left</span>
      </div>
      <div className="h-2.5 bg-muted dark:bg-stone-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function EmployeePortalPage({ embedded = false }) {
  const Wrap = embedded ? Fragment : DashboardLayout;
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [payments, setPayments] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [requests, setRequests] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [myLoans, setMyLoans] = useState([]);
  const [attendanceSettings, setAttendanceSettings] = useState({ allowed_methods: ['manual', 'fingerprint', 'face', 'watch'], geofence: { enabled: false, locations: [] } });
  const [selectedMethod, setSelectedMethod] = useState('manual');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoStatus, setGeoStatus] = useState(null); // null | 'inside' | 'outside' | 'error'
  const [workShifts, setWorkShifts] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [salarySummary, setSalarySummary] = useState([]);
  const [myPayslips, setMyPayslips] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [leaveData, setLeaveData] = useState({ leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '', with_ticket: false });
  const [requestData, setRequestData] = useState({ request_type: 'letter', subject: '', details: '', amount: '', date: '', end_date: '', hours: '' });
  const [editData, setEditData] = useState({ phone: '', email: '' });
  const [noProfile, setNoProfile] = useState(false);
  const [portalTab, setPortalTab] = useState('attendance');
  const [notLinked, setNotLinked] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const profRes = await api.get('/my/employee-profile');
      setProfile(profRes.data);
      setEditData({ phone: profRes.data.phone || '', email: profRes.data.email || '' });
      const [payRes, leaveRes, reqRes, attRes, loanRes, taskRes, salSumRes, payslipRes, docRes] = await Promise.all([
        api.get('/my/payments'), api.get('/my/leaves'), api.get('/my/requests'),
        api.get('/my/attendance'), api.get('/my/loans').catch(() => ({ data: [] })),
        api.get('/task-reminders/my-reminders').catch(() => ({ data: [] })),
        api.get('/my/salary-summary').catch(() => ({ data: { summary: [] } })),
        api.get('/my/payslips').catch(() => ({ data: [] })),
        api.get('/my/documents').catch(() => ({ data: [] })),
      ]);
      setPayments(payRes.data); setLeaves(leaveRes.data); setRequests(reqRes.data);
      setAttendance(attRes.data); setMyLoans(loanRes.data); setMyTasks(taskRes.data);
      setSalarySummary(salSumRes.data?.summary || []);
      setMyPayslips(payslipRes.data || []);
      setMyDocuments(docRes.data || []);
      // Fetch attendance settings and work shifts
      const [settRes, shiftRes] = await Promise.all([
        api.get('/attendance/settings').catch(() => ({ data: { allowed_methods: ['manual', 'fingerprint', 'face', 'watch'] } })),
        api.get('/work-shifts').catch(() => ({ data: [] })),
      ]);
      setAttendanceSettings(settRes.data);
      setWorkShifts(Array.isArray(shiftRes.data) ? shiftRes.data : []);
      const today = localToday();
      setTodayAttendance(attRes.data.find(a => a.date === today) || null);
    } catch (err) {
      if (err.response?.status === 404) setNoProfile(true);
    } finally {
      // Always fetch notifications, even without an employee profile
      try {
        const notifRes = await api.get('/my/notifications');
        setNotifications(notifRes.data?.notifications || []);
        setUnreadCount(notifRes.data?.unread_count || 0);
      } catch {}
      setLoading(false);
    }
  };

  const getLocationAndPost = async (endpoint, successMsg) => {
    const geofenceEnabled = attendanceSettings?.geofence?.enabled;
    const payload = { method: selectedMethod };
    if (geofenceEnabled) {
      if (!navigator.geolocation) {
        toast.error('GPS not supported on this device');
        return;
      }
      setGeoLoading(true);
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
        });
        payload.latitude = pos.coords.latitude;
        payload.longitude = pos.coords.longitude;
      } catch (geoErr) {
        setGeoLoading(false);
        setGeoStatus('error');
        toast.error('Location access denied. Enable GPS and try again.');
        return;
      }
    }
    try {
      const res = await api.post(endpoint, payload);
      setGeoStatus(geofenceEnabled ? 'inside' : null);
      toast.success(successMsg);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      if (detail.includes('away from') || detail.includes('Location required')) {
        setGeoStatus('outside');
      }
      if (isNotLinked(err)) setNotLinked(true); else toast.error(detail);
    } finally {
      setGeoLoading(false);
    }
  };

  const handleTimeIn = () => getLocationAndPost('/attendance/time-in', `Timed in via ${selectedMethod}!`);
  const handleTimeOut = () => getLocationAndPost('/attendance/time-out', `Timed out via ${selectedMethod}!`);
  const handleAcknowledgeTask = async (reminderId) => {
    try {
      await api.post(`/task-reminders/${reminderId}/acknowledge`);
      toast.success('Task acknowledged!');
      setMyTasks(prev => prev.map(t => t.id === reminderId ? { ...t, last_acknowledged: new Date().toISOString() } : t));
    } catch { toast.error('Failed to acknowledge'); }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post('/my/apply-leave', { ...leaveData, employee_id: '', days: parseInt(leaveData.days), with_ticket: leaveData.with_ticket, start_date: new Date(leaveData.start_date).toISOString(), end_date: new Date(leaveData.end_date).toISOString() });
      toast.success(leaveData.with_ticket ? 'Leave + ticket request submitted' : 'Leave request submitted');
      setShowLeaveForm(false); setLeaveData({ leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '', with_ticket: false }); fetchData();
    } catch (err) { if (isNotLinked(err)) setNotLinked(true); else toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const markNotifRead = async (notifId) => {
    try {
      await api.put(`/my/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/my/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch {}
  };


  const handleRequest = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        request_type: requestData.request_type,
        subject: requestData.subject,
        details: requestData.details,
        amount: requestData.amount ? parseFloat(requestData.amount) : null,
        date: requestData.date || null,
        end_date: requestData.end_date || null,
        hours: requestData.hours ? parseFloat(requestData.hours) : null,
      };
      await api.post('/my/request', payload);
      toast.success('Request submitted'); setShowRequestForm(false); setRequestData({ request_type: 'letter', subject: '', details: '', amount: '', date: '', end_date: '', hours: '' }); fetchData();
    } catch (err) { if (isNotLinked(err)) setNotLinked(true); else toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadPayslip = async (id) => {
    try {
      const res = await api.get(`/salary-payments/${id}/payslip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const l = document.createElement('a'); l.href = url; l.setAttribute('download', `payslip_${id}.pdf`); document.body.appendChild(l); l.click(); l.remove();
    } catch { toast.error('Failed'); }
  };
  const downloadLetter = async (type) => {
    try {
      const res = await api.post('/letters/generate', { employee_id: profile.id, letter_type: type }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const l = document.createElement('a'); l.href = url; l.setAttribute('download', `${type}_${profile.name}.pdf`); document.body.appendChild(l); l.click(); l.remove();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <Wrap><div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div></Wrap>;
  if (noProfile) return (
    <Wrap>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="no-profile-message">
        <div className="flex flex-col items-center justify-center text-center px-4 pt-12">
          <div className="w-16 h-16 rounded-full bg-warning/20 flex items-center justify-center mb-4">
            <User size={28} className="text-warning" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Employee Profile Not Linked</h2>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            Your account is not linked to an employee profile. Please contact your administrator to link your user account in HR &amp; Payroll → Employees.
          </p>
          <div className="bg-info/10 border border-info/30 rounded-xl p-4 max-w-md text-left text-xs space-y-1">
            <p className="font-semibold text-info">Ask your admin to:</p>
            <p className="text-info">1. Go to <strong>Employees</strong> page</p>
            <p className="text-info">2. Edit your employee record</p>
            <p className="text-info">3. Link it to your user account</p>
          </div>
        </div>
        {/* Show notifications even without profile */}
        {notifications.length > 0 && (
          <Card className="dark:bg-foreground dark:border-stone-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-base dark:text-white flex items-center gap-2">
                  <Bell size={16} className="text-orange-500" />
                  Notifications ({notifications.length})
                  {unreadCount > 0 && <span className="text-xs text-error font-normal">{unreadCount} unread</span>}
                </CardTitle>
                {unreadCount > 0 && (
                  <Button size="sm" variant="outline" className="text-xs" onClick={markAllRead}>
                    <CheckCircle size={12} className="mr-1" />Mark All Read
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {notifications.map(notif => (
                  <div key={notif.id}
                    className={`p-3 rounded-xl border transition-all cursor-pointer ${
                      notif.read ? 'dark:border-stone-700' : 'border-orange-200 bg-orange-50/50 dark:bg-orange-900/10'
                    }`}
                    onClick={() => !notif.read && markNotifRead(notif.id)}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        notif.read ? 'bg-muted' : 'bg-orange-100'
                      }`}>
                        <Clock size={14} className={notif.read ? 'text-muted-foreground' : 'text-orange-500'} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${notif.read ? 'text-muted-foreground' : ''}`}>{notif.title}</p>
                          {!notif.read && <span className="w-2 h-2 rounded-full bg-orange-500" />}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{notif.message}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Wrap>
  );

  const annualUsed = leaves.filter(l => l.leave_type === 'annual' && l.status === 'approved').reduce((s, l) => s + l.days, 0);
  const sickUsed = leaves.filter(l => l.leave_type === 'sick' && l.status === 'approved').reduce((s, l) => s + l.days, 0);
  const pendingPayments = payments.filter(p => !p.acknowledged && p.payment_type !== 'loan_repayment');
  const currentPeriod = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const paidThisMonth = payments.filter(p => p.period === currentPeriod && p.payment_type === 'salary').reduce((s, p) => s + p.amount, 0);
  const dueBalance = Math.max(0, (profile?.salary || 0) - paidThisMonth);
  const activeLoans = myLoans.filter(l => l.status === 'active');
  const totalLoanBalance = activeLoans.reduce((s, l) => s + (l.remaining_balance || 0), 0);

  return (
    <Wrap>
      <div className="space-y-5" data-testid="employee-portal-page">
        {/* Header with Time In/Out */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className={embedded ? 'hidden' : ''}>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

                <User size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="portal-title">My Portal</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Employee self-service portal</p>

              </div>

            </div>
            <p className="text-sm text-muted-foreground">Welcome back, {profile?.name}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Attendance Method Selector */}
            {(!todayAttendance?.time_in || (todayAttendance?.time_in && !todayAttendance?.time_out)) && (
              <div className="flex items-center gap-1 mr-1" data-testid="attendance-method-selector">
                {[
                  { key: 'manual', label: 'Manual', icon: '🖱' },
                  { key: 'fingerprint', label: 'Fingerprint', icon: '👆' },
                  { key: 'face', label: 'Face', icon: '👤' },
                  { key: 'watch', label: 'Watch', icon: '⌚' },
                ].filter(m => attendanceSettings.allowed_methods?.includes(m.key)).map(m => (
                  <button
                    key={m.key}
                    onClick={() => setSelectedMethod(m.key)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                      selectedMethod === m.key
                        ? 'bg-success/20 border-success/30 text-success dark:bg-emerald-900 dark:text-emerald-300'
                        : 'bg-white border-border text-muted-foreground hover:border-border dark:bg-muted dark:border-stone-600 dark:text-muted-foreground'
                    }`}
                    data-testid={`method-${m.key}`}
                  >
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>
            )}
            <NotLinkedNotice show={notLinked} />
            {!todayAttendance?.time_in ? (
              <Button onClick={handleTimeIn} disabled={geoLoading} className="bg-success hover:bg-success rounded-xl" data-testid="time-in-btn">
                {geoLoading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <LogIn size={15} className="mr-1.5" />}
                {geoLoading ? 'Locating...' : 'Time In'}
              </Button>
            ) : !todayAttendance?.time_out ? (
              <Button onClick={handleTimeOut} disabled={geoLoading} variant="outline" className="border-error/30 text-error rounded-xl" data-testid="time-out-btn">
                {geoLoading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <LogOut size={15} className="mr-1.5" />}
                {geoLoading ? 'Locating...' : 'Time Out'}
              </Button>
            ) : (
              <Badge className="bg-success/10 text-success dark:bg-emerald-900/30 dark:text-emerald-400 py-2 px-4 rounded-xl border-0"><CheckCircle size={14} className="mr-1.5" />Done for today</Badge>
            )}
            {attendanceSettings?.geofence?.enabled && (
              <div className="flex items-center gap-1 text-[10px]" data-testid="geofence-indicator">
                <MapPin size={12} className={geoStatus === 'inside' ? 'text-success' : geoStatus === 'outside' ? 'text-error' : 'text-warning'} />
                <span className={geoStatus === 'inside' ? 'text-success' : geoStatus === 'outside' ? 'text-error' : 'text-warning'}>
                  {geoStatus === 'inside' ? 'In zone' : geoStatus === 'outside' ? 'Outside zone' : 'GPS required'}
                </span>
              </div>
            )}
            <Button onClick={() => setShowLeaveForm(true)} variant="outline" size="sm" className="rounded-xl border-border"><Calendar size={14} className="mr-1" />Leave</Button>
            <Button onClick={() => setShowRequestForm(true)} variant="outline" size="sm" className="rounded-xl border-border"><Send size={14} className="mr-1" />Request</Button>
            <div className="relative">
              <Button variant="outline" size="sm" className={`rounded-xl ${unreadCount > 0 ? 'border-primary' : 'border-border'}`} onClick={() => {
                const el = document.querySelector('[data-testid="notif-tab"]');
                if (el) el.click();
              }} data-testid="notif-bell-btn">
                {unreadCount > 0 ? <BellRing size={14} className="text-primary" /> : <Bell size={14} />}
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-error text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Card + Leave Balance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Profile Info */}
          <div className="bg-white dark:bg-muted rounded-2xl shadow-sm p-5" data-testid="profile-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                  <User size={24} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-base font-outfit text-foreground dark:text-white truncate">{profile?.name}</h2>
                  <p className="text-xs text-muted-foreground">{profile?.position || 'Employee'}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-[11px] text-muted-foreground">
                    {profile?.phone && <span className="flex items-center gap-1"><Phone size={10} />{profile.phone}</span>}
                    {profile?.email && <span className="flex items-center gap-1"><Mail size={10} />{profile.email}</span>}
                    {profile?.branch_name && <span className="flex items-center gap-1"><Building size={10} />{profile.branch_name}</span>}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 text-[11px] h-7 px-2 text-primary hover:bg-primary/5 rounded-lg" onClick={() => setShowEditProfile(true)} data-testid="edit-profile-btn">
                    Edit Info
                  </Button>
                </div>
              </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-white dark:bg-muted rounded-2xl shadow-sm p-5" data-testid="financial-card">
              <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Financial</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground">Salary</p>
                  <p className="text-lg font-bold font-outfit text-foreground dark:text-white">{cur()} {(profile?.salary || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Due Balance</p>
                  <p className={`text-lg font-bold font-outfit ${dueBalance > 0 ? 'text-error' : 'text-success'}`}>
                    {cur()} {dueBalance.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Loan Balance</p>
                  <p className="text-lg font-bold font-outfit text-warning">{cur()} {totalLoanBalance.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Active Loans</p>
                  <p className="text-lg font-bold font-outfit text-foreground dark:text-white">{activeLoans.length}</p>
                </div>
              </div>
          </div>

          {/* Leave Balances */}
          <div className="bg-white dark:bg-muted rounded-2xl shadow-sm p-5" data-testid="leave-balance-card">
              <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Leave Balance</h3>
              <div className="space-y-3">
                <LeaveBalanceBar label="Annual Leave" used={annualUsed} total={profile?.annual_leave_entitled || 30} color="bg-success" />
                <LeaveBalanceBar label="Sick Leave" used={sickUsed} total={profile?.sick_leave_entitled || 15} color="bg-info" />
                <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-border dark:border-stone-700">
                  <span className="text-muted-foreground">Ticket Balance</span>
                  <span className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground">{(profile?.ticket_entitled || 1) - (profile?.ticket_used || 0)} remaining</span>
                </div>
              </div>
          </div>
        </div>

        {/* Pending Payments Alert */}
        {pendingPayments.length > 0 && (
          <Card className="border-warning/30 bg-warning/10 dark:bg-amber-900/10 dark:border-amber-800" data-testid="pending-payments-alert">
            <CardContent className="p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3 dark:text-amber-300"><DollarSign size={16} className="text-warning" />Payments Awaiting Confirmation</h3>
              <div className="space-y-2">
                {pendingPayments.map(p => (
                  <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-white dark:bg-muted rounded-xl border dark:border-stone-700">
                    <div>
                      <span className="font-medium text-sm dark:text-white">{cur()} {p.amount.toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground ml-2">{(p.payment_type || 'salary').replace('_', ' ')} &middot; {p.period}</span>
                    </div>
                    <Button size="sm" onClick={() => setPortalTab('payments')} data-testid={`pending-review-btn-${p.id}`}><CheckCircle size={14} className="mr-1" />Review &amp; Confirm</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={portalTab} onValueChange={setPortalTab}>
          <div className="overflow-x-auto">
            <TabsList className="w-full sm:w-auto flex-wrap">
              <TabsTrigger value="attendance" className="text-xs sm:text-sm">Attendance</TabsTrigger>
              <TabsTrigger value="salary-record" className="text-xs sm:text-sm" data-testid="salary-record-tab">Salary Record</TabsTrigger>
              <TabsTrigger value="payslips" className="text-xs sm:text-sm" data-testid="payslips-tab">Payslips</TabsTrigger>
              <TabsTrigger value="payments" className="text-xs sm:text-sm" data-testid="payments-tab">Payments</TabsTrigger>
              <TabsTrigger value="leaves" className="text-xs sm:text-sm">Leaves</TabsTrigger>
              <TabsTrigger value="my-docs" className="text-xs sm:text-sm" data-testid="my-docs-tab">My Documents</TabsTrigger>
              <TabsTrigger value="loans" className="text-xs sm:text-sm">Loans ({myLoans.length})</TabsTrigger>
              <TabsTrigger value="requests" className="text-xs sm:text-sm" data-testid="self-services-tab">Self Services</TabsTrigger>
              <TabsTrigger value="letters" className="text-xs sm:text-sm">Letters</TabsTrigger>
              <TabsTrigger value="tasks" className="text-xs sm:text-sm">My Tasks</TabsTrigger>
              <TabsTrigger value="notifications" className="text-xs sm:text-sm" data-testid="notif-tab-2">
                Notifications {unreadCount > 0 && <span className="ml-1 w-5 h-5 bg-error text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center">{unreadCount}</span>}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="attendance">
            <div className="space-y-4">
              <Card className="dark:bg-foreground dark:border-stone-700">
                <CardHeader><CardTitle className="font-outfit text-base dark:text-white">Attendance Records</CardTitle></CardHeader>
                <CardContent className="p-0 sm:p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="attendance-table">
                      <thead><tr className="border-b dark:border-stone-700">
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">In</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Method</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Out</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Method</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Hours</th>
                      </tr></thead>
                      <tbody>{attendance.slice(0, 30).map(a => {
                        const tin = a.time_in ? new Date(a.time_in) : null;
                        const tout = a.time_out ? new Date(a.time_out) : null;
                        const hours = tin && tout ? ((tout - tin) / 3600000).toFixed(1) : '-';
                        return (<tr key={a.id} className="border-b dark:border-stone-700 hover:bg-muted/50 dark:hover:bg-muted">
                          <td className="p-3 text-sm dark:text-muted-foreground">{a.date}</td>
                          <td className="p-3 text-sm text-success">{tin ? format(tin, 'hh:mm a') : '-'}</td>
                          <td className="p-3"><Badge variant="secondary" className="text-[10px] capitalize">{a.method_in || 'manual'}</Badge></td>
                          <td className="p-3 text-sm text-error">{tout ? format(tout, 'hh:mm a') : '-'}</td>
                          <td className="p-3"><Badge variant="secondary" className="text-[10px] capitalize">{a.method_out || '-'}</Badge></td>
                          <td className="p-3 text-sm font-medium dark:text-white">{hours}h</td>
                        </tr>);
                      })}{attendance.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No records</td></tr>}</tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Work Shifts */}
              <Card className="dark:bg-foreground dark:border-stone-700" data-testid="work-shifts-card">
                <CardHeader><CardTitle className="font-outfit text-base dark:text-white flex items-center gap-2"><Clock size={16} className="text-info" /> Work Shifts</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {workShifts.map(s => (
                      <div key={s.id} className="p-3 rounded-xl border dark:border-stone-700 dark:bg-muted" data-testid={`shift-${s.id}`}>
                        <p className="text-sm font-semibold dark:text-white">{s.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.start} - {s.end}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Break: {s.break_minutes}min &middot; {(s.days || []).join(', ')}</p>
                      </div>
                    ))}
                    {workShifts.length === 0 && <p className="text-sm text-muted-foreground col-span-3 text-center py-4">No work shifts configured</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="salary-record">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader>
                <CardTitle className="font-outfit text-base dark:text-white flex items-center gap-2">
                  <Receipt size={18} className="text-orange-500" />Monthly Salary Record
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                {salarySummary.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Banknote size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No salary records found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="salary-record-table">
                      <thead>
                        <tr className="border-b dark:border-stone-700">
                          <th className="text-left p-3 text-xs font-medium text-muted-foreground">Month</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground">Salary</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground">Paid</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground hidden sm:table-cell">Extras</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground hidden sm:table-cell">Deductions</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground">Net</th>
                          <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                          <th className="text-left p-3 text-xs font-medium text-muted-foreground hidden sm:table-cell">Paid On</th>
                          <th className="text-center p-3 text-xs font-medium text-muted-foreground hidden sm:table-cell">Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salarySummary.map((row) => {
                          const extras = row.advance + row.overtime + row.bonus;
                          return (
                            <tr key={row.period} className="border-b dark:border-stone-700 hover:bg-muted/50 dark:hover:bg-muted" data-testid={`salary-row-${row.period}`}>
                              <td className="p-3 text-sm font-medium dark:text-white">{row.period}</td>
                              <td className="p-3 text-sm text-right dark:text-muted-foreground">{cur()} {row.monthly_salary.toLocaleString()}</td>
                              <td className="p-3 text-sm text-right font-medium text-success">{cur()} {row.salary_paid.toLocaleString()}</td>
                              <td className="p-3 text-sm text-right dark:text-muted-foreground hidden sm:table-cell">
                                {extras > 0 ? <span className="text-info">+SR {extras.toLocaleString()}</span> : '-'}
                              </td>
                              <td className="p-3 text-sm text-right hidden sm:table-cell">
                                {row.deductions > 0 ? <span className="text-error">-SR {row.deductions.toLocaleString()}</span> : '-'}
                              </td>
                              <td className="p-3 text-sm text-right font-bold dark:text-white">{cur()} {row.total_received.toLocaleString()}</td>
                              <td className="p-3 text-center">
                                {row.status === 'paid' ? (
                                  <Badge className="bg-success/20 text-success dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                                    <CheckCircle size={10} className="mr-0.5" />Paid
                                  </Badge>
                                ) : row.status === 'partial' ? (
                                  <Badge className="bg-warning/20 text-warning dark:bg-amber-900/30 dark:text-amber-400 text-[10px]">
                                    <AlertTriangle size={10} className="mr-0.5" />Partial
                                  </Badge>
                                ) : (
                                  <Badge className="bg-error/20 text-error dark:bg-red-900/30 dark:text-red-400 text-[10px]">
                                    <XCircle size={10} className="mr-0.5" />Unpaid
                                  </Badge>
                                )}
                              </td>
                              <td className="p-3 text-sm dark:text-muted-foreground hidden sm:table-cell">
                                {row.payment_date ? format(new Date(row.payment_date), 'MMM dd, yyyy') : '-'}
                              </td>
                              <td className="p-3 text-center hidden sm:table-cell">
                                {row.payment_mode ? <Badge variant="secondary" className="capitalize text-[10px]">{row.payment_mode}</Badge> : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="p-4 border-t dark:border-stone-700 flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span>Total Months: <strong className="dark:text-white">{salarySummary.length}</strong></span>
                      <span>Total Received: <strong className="text-success">{cur()} {salarySummary.reduce((s, r) => s + r.total_received, 0).toLocaleString()}</strong></span>
                      <span>Fully Paid: <strong className="dark:text-white">{salarySummary.filter(r => r.status === 'paid').length}</strong></span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payslips" data-testid="payslips-content">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader><CardTitle className="font-outfit text-base dark:text-white">My Payslips</CardTitle></CardHeader>
              <CardContent>
                {myPayslips.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">No payslips generated yet.</div>
                ) : (
                  <div className="space-y-3">
                    {myPayslips.map((slip) => (
                      <div key={slip.id} className="border rounded-lg p-3 sm:p-4 dark:border-stone-700" data-testid={`payslip-${slip.id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div className="font-medium text-sm">{slip.period}</div>
                          <Badge variant="default" className="text-xs w-fit">Net: {slip.net_salary?.toLocaleString()}</Badge>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground dark:text-muted-foreground">
                          <div>Basic: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.basic_salary?.toLocaleString()}</span></div>
                          <div>Housing: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.housing_allowance?.toLocaleString()}</span></div>
                          <div>Transport: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.transport_allowance?.toLocaleString()}</span></div>
                          <div>Gross: <span className="text-success font-medium">{slip.gross_salary?.toLocaleString()}</span></div>
                          <div>GOSI: <span className="text-error font-medium">-{slip.gosi_deduction?.toLocaleString()}</span></div>
                          <div>Deductions: <span className="text-error font-medium">-{slip.total_deductions?.toLocaleString()}</span></div>
                          <div>Days: <span className="font-medium">{slip.working_days}</span></div>
                          <div>OT: <span className="font-medium">{slip.overtime_pay?.toLocaleString()}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <PaymentConfirmationsPanel onChanged={fetchData} />
          </TabsContent>

          <TabsContent value="leaves">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-outfit text-base dark:text-white">Leave History</CardTitle>
                <Button size="sm" onClick={() => setShowLeaveForm(true)}><Calendar size={14} className="mr-1" />Apply</Button>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="leaves-table">
                    <thead><tr className="border-b dark:border-stone-700">
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Type</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Dates</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Days</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                    </tr></thead>
                    <tbody>{leaves.map(l => (
                      <tr key={l.id} className="border-b dark:border-stone-700 hover:bg-muted/50 dark:hover:bg-muted">
                        <td className="p-3"><Badge variant="secondary" className="capitalize text-xs">{l.leave_type}</Badge>{l.with_ticket && <Badge className="ml-1 bg-info/20 text-info text-[10px]">Ticket</Badge>}</td>
                        <td className="p-3 text-sm dark:text-muted-foreground">{format(new Date(l.start_date), 'MMM dd')} - {format(new Date(l.end_date), 'MMM dd')}</td>
                        <td className="p-3 text-center font-medium dark:text-white">{l.days}</td>
                        <td className="p-3 text-center">{getStatusBadge(l.status)}</td>
                      </tr>
                    ))}{leaves.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No leaves</td></tr>}</tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="my-docs" data-testid="my-docs-content">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader><CardTitle className="font-outfit text-base dark:text-white">My Documents</CardTitle></CardHeader>
              <CardContent>
                {myDocuments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">No documents on file.</div>
                ) : (
                  <div className="space-y-2">
                    {myDocuments.map((doc) => (
                      <div key={doc.id} className="border rounded-lg p-3 dark:border-stone-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`my-doc-${doc.id}`}>
                        <div>
                          <div className="font-medium text-sm">{doc.title}</div>
                          <div className="text-xs text-muted-foreground dark:text-muted-foreground flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-[10px] capitalize">{doc.category?.replace(/_/g, ' ')}</Badge>
                            {doc.document_number && <span>#{doc.document_number}</span>}
                            {doc.expiry_date && (
                              <Badge variant={doc.expiry_date < localToday() ? 'destructive' : 'secondary'} className="text-[10px]">
                                Expires: {doc.expiry_date}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {doc.file_url && (
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View File</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="loans">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader><CardTitle className="font-outfit text-base dark:text-white">My Loans</CardTitle></CardHeader>
              <CardContent>
                {myLoans.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><Wallet size={40} className="mx-auto mb-2 opacity-30" /><p>No loans</p></div>
                ) : (
                  <div className="space-y-3">
                    {myLoans.map(loan => {
                      const progress = loan.amount > 0 ? ((loan.amount - (loan.remaining_balance || 0)) / loan.amount) * 100 : 0;
                      return (
                        <div key={loan.id} className="p-4 border dark:border-stone-700 rounded-xl dark:bg-muted" data-testid={`my-loan-${loan.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary" className="capitalize text-xs">{(loan.loan_type || 'loan').replace('_', ' ')}</Badge>
                              {getStatusBadge(loan.status)}
                            </div>
                            <div className="text-right">
                              <p className="font-bold font-outfit dark:text-white">{cur()} {loan.amount.toLocaleString()}</p>
                              {loan.status === 'active' && <p className="text-xs text-muted-foreground">Remaining: {cur()} {(loan.remaining_balance || 0).toLocaleString()}</p>}
                            </div>
                          </div>
                          {loan.status === 'active' && (
                            <div className="mt-3">
                              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                <span>{loan.paid_installments || 0}/{loan.total_installments || '?'} paid</span>
                                <span>{cur()} {(loan.monthly_installment || 0).toLocaleString()}/mo</span>
                              </div>
                              <div className="h-2 bg-muted dark:bg-stone-700 rounded-full overflow-hidden">
                                <div className="h-full bg-success rounded-full transition-all" style={{ width: `${progress}%` }} />
                              </div>
                            </div>
                          )}
                          {loan.reason && <p className="text-xs text-muted-foreground mt-2">Reason: {loan.reason}</p>}
                          {(loan.installments || []).length > 0 && (
                            <div className="mt-3 border-t dark:border-stone-700 pt-2" data-testid={`loan-deduction-history-${loan.id}`}>
                              <p className="text-xs font-medium text-muted-foreground mb-1.5">Deduction History ({loan.installments.length})</p>
                              <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                                {loan.installments.map((inst, idx) => (
                                  <div key={inst.id || idx} className="flex items-center justify-between gap-2 text-xs" data-testid={`loan-installment-row-${idx}`}>
                                    <span className="text-muted-foreground truncate">
                                      {(inst.date || '').slice(0, 10)}
                                      {inst.period ? ` · ${inst.period}` : ''}
                                      {inst.payment_mode === 'deduction' ? ' · payroll auto-deduct' : inst.payment_mode ? ` · ${inst.payment_mode}` : ''}
                                    </span>
                                    <span className="font-mono font-medium text-rose-600 dark:text-rose-400 whitespace-nowrap">−{cur()} {(inst.amount || 0).toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="requests">
            <div className="space-y-4">
              {/* Self Services Catalog */}
              <Card className="dark:bg-foreground dark:border-stone-700">
                <CardHeader>
                  <CardTitle className="font-outfit text-base dark:text-white">Self Services</CardTitle>
                  <p className="text-xs text-muted-foreground">Submit requests that go to your manager for approval</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="self-services-grid">
                    {[
                      { type: 'missing_punch', title: 'Missing Punch', icon: Hand, color: 'text-orange-500', desc: 'Report a missed clock-in/out' },
                      { type: 'excuse', title: 'Excuse', icon: Coffee, color: 'text-warning', desc: 'Request time away during work' },
                      { type: 'change_workshift', title: 'Change Workshift', icon: Repeat, color: 'text-info', desc: 'Request a shift change' },
                      { type: 'overtime', title: 'Overtime', icon: Clock, color: 'text-indigo-500', desc: 'Submit overtime hours' },
                      { type: 'travel_ticket', title: 'Travel Ticket', icon: Plane, color: 'text-info', desc: 'Request travel arrangements' },
                      { type: 'perdiem', title: 'Perdiem', icon: CreditCard, color: 'text-success', desc: 'Daily travel allowance' },
                      { type: 'purchase_request', title: 'Purchase Request', icon: Receipt, color: 'text-violet-500', desc: 'Request a purchase' },
                      { type: 'loan', title: 'Loan', icon: Banknote, color: 'text-success', desc: 'Request a loan advance' },
                      { type: 'benefit_disbursement', title: 'Benefit Disbursement', icon: HeartPulse, color: 'text-pink-500', desc: 'Claim a benefit' },
                      { type: 'out_of_office', title: 'Out of Office', icon: CalendarOff, color: 'text-teal-500', desc: 'Set OOO status' },
                      { type: 'training_course', title: 'Request Training', icon: GraduationCap, color: 'text-purple-500', desc: 'Enroll in a training course' },
                      { type: 'resignation', title: 'Resignation', icon: LogOut, color: 'text-error', desc: 'Submit resignation notice' },
                      { type: 'grievance', title: 'Grievance', icon: ShieldAlert, color: 'text-error', desc: 'File a formal grievance' },
                      { type: 'fixation_salary', title: 'Fixation Salary', icon: ArrowUpDown, color: 'text-cyan-500', desc: 'Request salary adjustment' },
                      { type: 'change_off_day', title: 'Change Off Day', icon: CalendarOff, color: 'text-lime-600', desc: 'Request off day change' },
                      { type: 'salary_advance', title: 'Salary Advance', icon: DollarSign, color: 'text-warning', desc: 'Request early salary' },
                    ].map(svc => (
                      <button
                        key={svc.type}
                        onClick={() => { setRequestData({ request_type: svc.type, subject: svc.title, details: '', amount: '', date: '', end_date: '', hours: '' }); setShowRequestForm(true); }}
                        className="flex items-center gap-2.5 p-3 rounded-xl border dark:border-stone-700 hover:bg-muted/50 dark:hover:bg-muted transition-all text-left"
                        data-testid={`svc-${svc.type}`}
                      >
                        <svc.icon size={20} className={svc.color} />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold dark:text-white truncate">{svc.title}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{svc.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* My Submitted Requests */}
              <Card className="dark:bg-foreground dark:border-stone-700">
                <CardHeader>
                  <CardTitle className="font-outfit text-base dark:text-white flex items-center gap-2">
                    <ClipboardList size={16} /> My Requests ({requests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">{requests.map(r => (
                    <div key={r.id} className="p-3 border dark:border-stone-700 rounded-xl dark:bg-muted">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm dark:text-white">{r.subject}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge variant="secondary" className="capitalize text-xs">{r.request_type.replace(/_/g, ' ')}</Badge>
                            {r.amount && <span className="text-xs text-muted-foreground">{cur()} {r.amount}</span>}
                            {r.created_at && <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>}
                          </div>
                          {r.response && <p className="text-xs text-info dark:text-blue-400 mt-1">Response: {r.response}</p>}
                        </div>
                        {getStatusBadge(r.status)}
                      </div>
                    </div>
                  ))}{requests.length === 0 && <p className="text-center text-muted-foreground py-8">No requests yet. Use the services above to submit one.</p>}</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="letters">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader><CardTitle className="font-outfit text-base dark:text-white">Download Letters</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-4">Generate official letters with your details pre-filled.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { type: 'salary_certificate', title: 'Salary Certificate', desc: 'For bank, visa applications' },
                    { type: 'employment', title: 'Employment Letter', desc: 'Proof of employment' },
                    { type: 'noc', title: 'No Objection Certificate', desc: 'NOC for various purposes' },
                    { type: 'experience', title: 'Experience Certificate', desc: 'Work experience proof' },
                  ].map(l => (
                    <div key={l.type} className="p-4 border dark:border-stone-700 rounded-xl hover:bg-muted/50 dark:hover:bg-muted transition-all flex justify-between items-center">
                      <div><p className="font-medium text-sm dark:text-white">{l.title}</p><p className="text-xs text-muted-foreground">{l.desc}</p></div>
                      <Button size="sm" variant="outline" onClick={() => downloadLetter(l.type)}><FileText size={14} className="mr-1" />PDF</Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader><CardTitle className="font-outfit text-base dark:text-white">My Task Reminders ({myTasks.length})</CardTitle></CardHeader>
              <CardContent>
                {myTasks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No task reminders assigned to you</p>
                ) : (
                  <div className="space-y-2">
                    {myTasks.map(task => (
                      <div key={task.id} className="p-3 rounded-xl border dark:border-stone-700 flex items-center justify-between" data-testid={`my-task-${task.id}`}>
                        <div className="flex-1">
                          <p className="text-sm font-medium dark:text-white">{task.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{task.message}</p>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                            <span className="flex items-center gap-0.5"><Clock size={10} />Every {task.interval_hours}h</span>
                            <span>{task.active_start_hour}:00–{task.active_end_hour}:00</span>
                            {task.last_acknowledged && <span className="text-success">Acknowledged: {new Date(task.last_acknowledged).toLocaleTimeString()}</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-xl border-success/30 text-success hover:bg-success/10" onClick={() => handleAcknowledgeTask(task.id)} data-testid={`ack-task-${task.id}`}>
                          <CheckCircle size={14} className="mr-1" />Done
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" data-testid="notifications-tab">
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-base dark:text-white flex items-center gap-2">
                    <Bell size={16} className="text-orange-500" />
                    Notifications ({notifications.length})
                    {unreadCount > 0 && <span className="text-xs text-error font-normal">{unreadCount} unread</span>}
                  </CardTitle>
                  {unreadCount > 0 && (
                    <Button size="sm" variant="outline" className="text-xs" onClick={markAllRead} data-testid="mark-all-read-btn">
                      <CheckCircle size={12} className="mr-1" />Mark All Read
                    </Button>
                  )}
                  <a href="/notification-preferences">
                    <Button size="sm" variant="ghost" className="text-xs" data-testid="prefs-link-btn">Preferences</Button>
                  </a>
                </div>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Bell size={36} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No notifications yet</p>
                    <p className="text-xs">Duty reminders and alerts will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notifications.map(notif => (
                      <div key={notif.id}
                        className={`p-3 rounded-xl border transition-all cursor-pointer ${
                          notif.read 
                            ? 'dark:border-stone-700 bg-transparent' 
                            : 'border-orange-200 bg-orange-50/50 dark:bg-orange-900/10 dark:border-orange-800'
                        }`}
                        onClick={() => !notif.read && markNotifRead(notif.id)}
                        data-testid={`notification-${notif.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            notif.read ? 'bg-muted dark:bg-muted' : 'bg-orange-100 dark:bg-orange-900/30'
                          }`}>
                            {notif.type === 'task_reminder' ? (
                              <Clock size={14} className={notif.read ? 'text-muted-foreground' : 'text-orange-500'} />
                            ) : (
                              <Bell size={14} className={notif.read ? 'text-muted-foreground' : 'text-orange-500'} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`text-sm font-medium ${notif.read ? 'text-muted-foreground' : 'dark:text-white'}`}>{notif.title}</p>
                              {!notif.read && <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{notif.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Leave Dialog */}
        <Dialog open={showLeaveForm} onOpenChange={setShowLeaveForm}>
          <DialogContent><DialogHeader><DialogTitle className="font-outfit">Apply for Leave</DialogTitle></DialogHeader>
            <form onSubmit={handleApplyLeave} className="space-y-4">
              <NotLinkedNotice show={notLinked} />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type *</Label><Select value={leaveData.leave_type} onValueChange={(v) => setLeaveData({ ...leaveData, leave_type: v })}><SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="annual">Annual</SelectItem><SelectItem value="sick">Sick</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
                <div><Label>From *</Label><Input type="date" value={leaveData.start_date} onChange={(e) => { const s = e.target.value; const days = s && leaveData.end_date ? Math.max(1, Math.round((new Date(leaveData.end_date) - new Date(s)) / 86400000) + 1) : ''; setLeaveData({ ...leaveData, start_date: s, days }); }} required data-testid="leave-start" /></div>
                <div><Label>To *</Label><Input type="date" value={leaveData.end_date} onChange={(e) => { const en = e.target.value; const days = leaveData.start_date && en ? Math.max(1, Math.round((new Date(en) - new Date(leaveData.start_date)) / 86400000) + 1) : ''; setLeaveData({ ...leaveData, end_date: en, days }); }} required data-testid="leave-end" /></div>
                <div><Label>Days</Label><Input type="number" value={leaveData.days} readOnly className="bg-muted/50 dark:bg-muted font-bold" /></div>
              </div>
              <div><Label>Reason</Label><Textarea value={leaveData.reason} onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })} data-testid="leave-reason" /></div>
              {(profile?.ticket_entitled || 1) - (profile?.ticket_used || 0) > 0 && (
                <div className="flex items-center gap-3 p-3 bg-info/10 dark:bg-blue-900/20 rounded-xl border border-info/30 dark:border-blue-800"><Checkbox checked={leaveData.with_ticket} onCheckedChange={(v) => setLeaveData({ ...leaveData, with_ticket: v })} /><div><Label>Request ticket</Label><p className="text-xs text-muted-foreground">Balance: {(profile?.ticket_entitled || 1) - (profile?.ticket_used || 0)}</p></div></div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowLeaveForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-leave-btn">Submit Leave</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Request Dialog */}
        <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
          <DialogContent><DialogHeader><DialogTitle className="font-outfit">{requestData.subject || 'Submit Request'}</DialogTitle></DialogHeader>
            <form onSubmit={handleRequest} className="space-y-4">
              <NotLinkedNotice show={notLinked} />
              <div><Label>Type</Label><Select value={requestData.request_type} onValueChange={(v) => setRequestData({ ...requestData, request_type: v, subject: v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                <SelectItem value="missing_punch">Missing Punch</SelectItem>
                <SelectItem value="excuse">Excuse</SelectItem>
                <SelectItem value="change_workshift">Change Workshift</SelectItem>
                <SelectItem value="overtime">Overtime</SelectItem>
                <SelectItem value="travel_ticket">Travel Ticket</SelectItem>
                <SelectItem value="perdiem">Perdiem</SelectItem>
                <SelectItem value="purchase_request">Purchase Request</SelectItem>
                <SelectItem value="loan">Loan</SelectItem>
                <SelectItem value="benefit_disbursement">Benefit Disbursement</SelectItem>
                <SelectItem value="out_of_office">Out of Office</SelectItem>
                <SelectItem value="training_course">Request Training</SelectItem>
                <SelectItem value="resignation">Resignation</SelectItem>
                <SelectItem value="grievance">Grievance</SelectItem>
                <SelectItem value="fixation_salary">Fixation Salary</SelectItem>
                <SelectItem value="change_off_day">Change Off Day</SelectItem>
                <SelectItem value="salary_advance">Salary Advance</SelectItem>
                <SelectItem value="salary_certificate">Salary Certificate</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent></Select></div>

              {/* Date field for time-based requests */}
              {['missing_punch', 'excuse', 'overtime', 'out_of_office', 'change_off_day', 'change_workshift'].includes(requestData.request_type) && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date *</Label><Input type="date" value={requestData.date} onChange={(e) => setRequestData({ ...requestData, date: e.target.value })} required data-testid="request-date" /></div>
                  {['excuse', 'out_of_office'].includes(requestData.request_type) && (
                    <div><Label>End Date</Label><Input type="date" value={requestData.end_date} onChange={(e) => setRequestData({ ...requestData, end_date: e.target.value })} data-testid="request-end-date" /></div>
                  )}
                  {requestData.request_type === 'overtime' && (
                    <div><Label>Hours *</Label><Input type="number" step="0.5" min="0.5" value={requestData.hours} onChange={(e) => setRequestData({ ...requestData, hours: e.target.value })} required data-testid="request-hours" /></div>
                  )}
                </div>
              )}

              {/* Amount for financial requests */}
              {['loan', 'salary_advance', 'purchase_request', 'perdiem', 'benefit_disbursement', 'fixation_salary', 'travel_ticket'].includes(requestData.request_type) && (
                <div><Label>Amount (SR)</Label><Input type="number" step="0.01" value={requestData.amount} onChange={(e) => setRequestData({ ...requestData, amount: e.target.value })} data-testid="request-amount" /></div>
              )}

              <div><Label>Subject *</Label><Input value={requestData.subject} onChange={(e) => setRequestData({ ...requestData, subject: e.target.value })} required data-testid="request-subject" /></div>
              <div><Label>Details / Reason</Label><Textarea value={requestData.details} onChange={(e) => setRequestData({ ...requestData, details: e.target.value })} placeholder="Provide details or reason for this request..." /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRequestForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-request-btn">Submit Request</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Profile Dialog */}
        <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-outfit">Edit Personal Info</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Phone</Label><Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} data-testid="edit-phone" /></div>
              <div><Label>Email</Label><Input value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} data-testid="edit-email" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditProfile(false)}>Cancel</Button>
              <Button onClick={async () => {
                try {
                  await api.put(`/employees/${profile.id}`, { ...profile, phone: editData.phone, email: editData.email });
                  toast.success('Profile updated'); setShowEditProfile(false); fetchData();
                } catch { toast.error('Failed to update'); }
              }} data-testid="save-profile-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
}
